/**
 * Radio component — shared types and Zod prop schema.
 *
 * A single round radio dot (the visible row label is rendered by the consumer,
 * not inside the dot). Unchecked: a bordered white circle; checked: a filled
 * dark circle with a tick glyph (per Figma — a check, not a bare dot).
 *
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { radius } from '../../elements/radius';
import { sizing } from '../../elements/sizing';
import type { IconName } from '../../elements/icons/registry';

/** Catalog-facing schema (plain ZodObject so `formatZodType` can introspect it). */
export const radioPropsSchema = z.object({
  checked: z.boolean().default(false),
  disabled: z.boolean().default(false),
  accessibilityLabel: z.string(),
});

export type RadioProps = z.infer<typeof radioPropsSchema>;

/** Dot diameter — Figma 24×24. */
export const RADIO_SIZE = sizing.icon.sm;

/** Border width of the unchecked circle. */
export const RADIO_BORDER_WIDTH = 1;

// Fully round corners come from `radius.rounded` (100), which exceeds half the
// 24px box so the square renders as a circle regardless of the exact size.
export const RADIO_RADIUS = radius.rounded;

// Figma's checked radio is the "check with container" glyph — a filled dark
// disc with a cut-out check — so the icon fills the whole dot (its cut-out
// reads against the light backing below).
export const RADIO_TICK_ICON: IconName = 'tick-with-container';

/** Tick glyph fills the 24px dot (it is the dark disc, not a mark inside it). */
export const RADIO_TICK_SIZE = RADIO_SIZE;

// Expands the 24px dot to a ≥44×44 touch target (ADR-025 Rule 5). Derived from
// the touch-target minimum so there are no raw size literals: (48-24)/2.
export const RADIO_HIT_SLOP = (sizing.touchTarget.minimum - RADIO_SIZE) / 2;

export const RADIO_BORDER_COLOR = colors.stroke.divider;
export const RADIO_BACKGROUND_UNCHECKED = colors.background.container;
export const RADIO_BACKGROUND_CHECKED = colors.button.primary;
// The dark disc comes from the tick-with-container glyph; its cut-out check
// shows the light dot backing through it.
export const RADIO_TICK_COLOR = colors.icon.primary;

/**
 * Dot fill + border for a given checked state. Both states keep the light
 * backing (the checked dark disc is drawn by the tick-with-container icon on
 * top); checked drops the divider ring since the icon covers the whole dot.
 */
export function radioDotColorsFor(checked: boolean): { background: string; border: string } {
  return checked
    ? { background: RADIO_BACKGROUND_UNCHECKED, border: RADIO_BACKGROUND_UNCHECKED }
    : { background: RADIO_BACKGROUND_UNCHECKED, border: RADIO_BORDER_COLOR };
}
